import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import Loader from '../../components/Loader';
import { useNotification } from '../../context/NotificationContext';
import { getTransactionById, uploadAdminMedia, addTransactionImages } from '../../services/adminApi';
import './AdminPages.css';

const TransactionDetails = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [newImages, setNewImages] = useState([]);

  const fetchTransactionDetails = useCallback(async () => {
    try {
      const response = await getTransactionById(transactionId);
      setTransaction(response.data);
      setImages(response.data.images || []);
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      showError('Failed to load transaction details');
      navigate('/admin/transactions');
    } finally {
      setLoading(false);
    }
  }, [transactionId, navigate, showError]);

  useEffect(() => {
    setLoading(true);
    fetchTransactionDetails();
  }, [fetchTransactionDetails]);

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadedImages = [];

    try {
      // First upload files to get URLs
      for (const file of files) {
        const formData = new FormData();
        formData.append('media', file);
        
        const response = await uploadAdminMedia(formData, (progress) => {
          console.log(`Upload progress: ${progress}%`);
        });
        
        if (response.data && response.data.url) {
          uploadedImages.push({
            url: response.data.url,
            name: file.name
          });
        }
      }

      // Then save images to transaction
      if (uploadedImages.length > 0) {
        await addTransactionImages(transactionId, uploadedImages);
        setNewImages(prev => [...prev, ...uploadedImages]);
        showSuccess(`${uploadedImages.length} image(s) uploaded and saved successfully`);
        // Refresh transaction data to show new images
        fetchTransactionDetails();
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      showError('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    handleImageUpload(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleImageUpload(files);
  };

  const removeNewImage = (index) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <AdminLayout pageTitle="Transaction Details" subtitle="Loading transaction information...">
        <Loader variant="dark" fullScreen={false} />
      </AdminLayout>
    );
  }

  const isBookingLedgerOnly = transaction?.source === 'booking';

  if (!transaction) {
    return (
      <AdminLayout pageTitle="Transaction Details" subtitle="Transaction not found">
        <div className="admin-page-container">
          <div className="admin-card">
            <p>Transaction not found.</p>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/admin/transactions')}
            >
              Back to Transactions
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout 
      pageTitle="Transaction Details" 
      subtitle={`Transaction ID: ${transaction.transactionId || transaction._id}`}
    >
      <div className="admin-page-container">
        {/* Transaction Information */}
        <div className="admin-card">
          <h3>Transaction Information</h3>
          <div className="transaction-details-grid">
            <div className="detail-item">
              <label>Transaction ID:</label>
              <span>{transaction.transactionId || transaction._id}</span>
            </div>
            <div className="detail-item">
              <label>Date:</label>
              <span>{formatDate(transaction.createdAt)}</span>
            </div>
            <div className="detail-item">
              <label>Status:</label>
              <span className={`status-badge ${transaction.status}`}>
                {transaction.status}
              </span>
            </div>
            <div className="detail-item">
              <label>Amount:</label>
              <span>{formatCurrency(transaction.amount)}</span>
            </div>
            <div className="detail-item">
                <label>Customer:</label>
                <span>
                  {transaction.user?.profile?.name || transaction.user?.mobile || 'N/A'}
                </span>
              </div>
              <div className="detail-item">
                <label>Service:</label>
                <span>
                  {transaction.cart?.services?.map((s) => s.name).filter(Boolean).join(', ') || 'N/A'}
                </span>
              </div>
              <div className="detail-item">
                <label>Stylist:</label>
                <span>
                  {transaction.cart?.stylist?.name || 'N/A'}
                </span>
              </div>
              <div className="detail-item">
                <label>Salon:</label>
                <span>
                  {transaction.cart?.salon?.name} {transaction.cart?.salon?.branch ? `(${transaction.cart?.salon.branch})` : ''}
                </span>
              </div>
          </div>
        </div>

        {/* Image management applies to payment gateway transactions only */}
        {!isBookingLedgerOnly ? (
          <div className="admin-card">
            <h3>Transaction Images</h3>
            
            <div 
              className="image-upload-area"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <div className="upload-content">
                <div className="upload-icon">+</div>
                <p>Drag and drop images here or click to select</p>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="btn btn-secondary">
                  Select Images
                </label>
              </div>
            </div>

            {uploading && (
              <div className="upload-progress">
                <Loader variant="dark" fullScreen={false} />
                <p>Uploading images...</p>
              </div>
            )}

            {images.length > 0 && (
              <div className="image-gallery">
                <h4>Existing Images</h4>
                <div className="image-grid">
                  {images.map((image, index) => (
                    <div key={index} className="image-item">
                    <img 
                      src={image.url} 
                      alt={`Upload ${index + 1}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div className="image-error" style={{ display: 'none' }}>
                        Failed to load image
                      </div>
                      <div className="image-info">
                        <p>{image.name || `Image ${index + 1}`}</p>
                        {image.uploadedAt && (
                          <small>{formatDate(image.uploadedAt)}</small>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {newImages.length > 0 && (
              <div className="image-gallery">
                <h4>Newly Uploaded Images</h4>
                <div className="image-grid">
                  {newImages.map((image, index) => (
                    <div key={index} className="image-item new-image">
                    <img 
                      src={image.url} 
                      alt={`New upload ${index + 1}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div className="image-error" style={{ display: 'none' }}>
                        Failed to load image
                      </div>
                      <div className="image-actions">
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={() => removeNewImage(index)}
                        >
                          Remove
                        </button>
                      </div>
                      <div className="image-info">
                        <p>{image.name}</p>
                        <small>Just uploaded</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {images.length === 0 && newImages.length === 0 && !uploading && (
              <div className="no-images">
                <p>No images uploaded for this transaction yet.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="admin-card">
            <p style={{ margin: 0, opacity: 0.85 }}>
              This row is from a completed booking payment (no separate transaction record). Images are not attached here.
            </p>
          </div>
        )}

        <div className="admin-card">
          <div className="action-buttons">
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/admin/transactions')}
            >
              Back to Transactions
            </button>
            {!isBookingLedgerOnly && newImages.length > 0 && (
              <button className="btn btn-primary">
                Save Changes
              </button>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .transaction-details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .detail-item label {
          font-weight: 600;
          color: #666;
          font-size: 0.875rem;
        }

        .detail-item span {
          font-size: 1rem;
        }

        .image-upload-area {
          border: 2px dashed #ccc;
          border-radius: 8px;
          padding: 2rem;
          text-align: center;
          margin-bottom: 2rem;
          cursor: pointer;
          transition: border-color 0.3s ease;
        }

        .image-upload-area:hover {
          border-color: #007bff;
        }

        .upload-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }

        .upload-icon {
          font-size: 3rem;
          color: #ccc;
          font-weight: 300;
        }

        .upload-progress {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 4px;
          margin-bottom: 1rem;
        }

        .image-gallery {
          margin-bottom: 2rem;
        }

        .image-gallery h4 {
          margin-bottom: 1rem;
          color: #333;
        }

        .image-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
        }

        .image-item {
          position: relative;
          border: 1px solid #ddd;
          border-radius: 8px;
          overflow: hidden;
          background: #fff;
        }

        .image-item img {
          width: 100%;
          height: 150px;
          object-fit: cover;
        }

        .image-item.new-image {
          border-color: #28a745;
        }

        .image-error {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 150px;
          background: #f8f9fa;
          color: #666;
          font-size: 0.875rem;
        }

        .image-actions {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
        }

        .image-info {
          padding: 0.75rem;
        }

        .image-info p {
          margin: 0;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .image-info small {
          color: #666;
          font-size: 0.75rem;
        }

        .no-images {
          text-align: center;
          padding: 2rem;
          color: #666;
        }

        .action-buttons {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 1rem;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .status-badge.completed {
          background: #d4edda;
          color: #155724;
        }

        .status-badge.pending {
          background: #fff3cd;
          color: #856404;
        }

        .status-badge.failed {
          background: #f8d7da;
          color: #721c24;
        }
      `}</style>
    </AdminLayout>
  );
};

export default TransactionDetails;
